import { Scene } from 'phaser';
import { AreaRolavel } from '../components/AreaRolavel';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima, desenharPainel, desenharParComPonto, desenharVoltar } from '../components/cenario';
import { desenharPostIt } from '../components/PostIt';
import { criarTextoRico } from '../components/TextoRico';
import { GAME, SCENES, TEX } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import {
    CORES_NOTA, criarGravadorMural, escreverResumido, formatarDataNota, sortearCor, temConteudo, TETO_NOTA
} from '../services/mural';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';


const CARTAO = { largura: 167, altura: 164, passoX: 207, passoY: 204 };
const AREA = { x: 52, y: 378, largura: 1228, altura: 392 };
const NOTA_ABERTA = { x: 425, y: 241, largura: 429, altura: 420 };

/**
 * 4. Mural de Insights (Figma, quadros 46–49). Cada participante vê só as
 * próprias notas (`RN-INS-01`); a nota só é publicada ao tocar em SALVAR.
 */
export class MuralScene extends Scene {
    constructor () {
        super(SCENES.MURAL);
    }

    /** `voltarPara`: cena que abriu esta página (uma fase em andamento, por exemplo). */
    init (dados = {}) {
        this.voltarPara = dados.voltarPara ?? SCENES.AGUARDE;
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.gravador = criarGravadorMural({ fonte: this.fonte });
        this.editor = null;
        this.fila = Promise.resolve();

        desenharPaginaLima(this, { titulo: t('mural.titulo'), rotuloJogador: Sessao.rotulo });
        desenharVoltar(this, 62, 185, () => !this.editor && this.scene.start(this.voltarPara, {}));
        this.add.rectangle(62, 216, 1156, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);
        criarTextoRico(this, 60, 242, 1160, t('mural.introducao'), ARCADE_TEXT.introducao);

        // RI-INS-01: mural navegável por rolagem horizontal.
        this.mural = new AreaRolavel(this, { ...AREA, eixo: 'x' });
        this.events.once('shutdown', () => this.fecharEditor({ redesenhar: false }));

        try {
            this.gravador.notas.push(...await this.fonte.listarInsights());
        } catch (falha) {
            console.error('[Mural] não foi possível carregar as notas.', falha);
            if (this.scene.isActive()) {
                this.add.text(640, 560, t('mural.erro_carregar'), ARCADE_TEXT.aviso)
                    .setOrigin(0.5);
            }
            return;
        }
        if (this.scene.isActive()) this.desenharMural();
    }

    // ------------------------------------------------------------------ mural

    desenharMural () {
        const { conteudo } = this.mural;
        conteudo.removeAll(true);

        // Primeira casa: "Adicionar nota"; depois as notas, da mais nova à mais antiga.
        const casas = [null, ...[...this.gravador.notas].reverse()];
        casas.forEach((nota, i) => {
            const x = 7 + Math.floor(i / 2) * CARTAO.passoX;
            const y = 8 + (i % 2) * CARTAO.passoY;
            if (nota) this.desenharCartao(nota, x, y);
            else this.desenharAdicionar(x, y);
        });

        const colunas = Math.ceil(casas.length / 2);
        this.mural.definirTamanho(7 + colunas * CARTAO.passoX);
    }

    desenharAdicionar (x, y) {
        const { conteudo } = this.mural;
        const g = this.add.graphics();
        desenharPostIt(g, x, y, CARTAO.largura, CARTAO.altura, CORES_NOTA.amarelo);
        g.fillStyle(ARCADE_HEX.TINTA, 1)
            .fillRect(x + 73, y + 54, 20, 2.5)
            .fillRect(x + 81.75, y + 45, 2.5, 20);
        conteudo.add([
            g,
            this.add.text(x + 83, y + 73, t('geral.adicionar_nota'), { ...ARCADE_TEXT.adicionarNota, letterSpacing: 1.5 })
                .setOrigin(0.5, 0)
        ]);
        this.tornarTocavel(x, y, () => this.abrirEditor(null));
    }

    desenharCartao (nota, x, y) {
        const { conteudo } = this.mural;
        const g = this.add.graphics();
        desenharPostIt(g, x, y, CARTAO.largura, CARTAO.altura, CORES_NOTA[nota.cor]);

        const texto = this.add.text(x + 83, y + 25, '', {
            // Quebra avançada: palavra maior que a nota é partida em vez de vazar para fora.
            ...ARCADE_TEXT.nota, wordWrap: { width: 142, useAdvancedWrap: true }
        })
            .setOrigin(0.5, 0);
        escreverResumido(texto, nota.texto, 5);

        conteudo.add([g, texto]);
        // RI-INS-07: tocar numa nota existente entra direto em edição.
        this.tornarTocavel(x, y, () => this.abrirEditor(nota));
    }

    tornarTocavel (x, y, aoTocar) {
        const zona = this.add.zone(x, y, CARTAO.largura, CARTAO.altura).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', (ponteiro) => {
                if (this.editor || this.mural.moveu || !this.mural.contem(ponteiro)) return;
                aoTocar();
            });
        this.mural.conteudo.add(zona);
    }

    // ----------------------------------------------------------------- editor

    /** RI-INS-02: "Adicionar nota" cria uma nota vazia já em edição. */
    abrirEditor (nota) {
        const emEdicao = nota ?? this.gravador.nova({
            cor: sortearCor(this.gravador.notas.map((n) => n.cor))      // RN-INS-02 / RN-INS-03
        });
        this.mural.ativa = false;

        const camada = this.add.container(0, 0).setDepth(100);
        const fundo = this.add.rectangle(GAME.CENTER_X, GAME.CENTER_Y, GAME.LARGURA_TOTAL, GAME.HEIGHT, 0x000000, 0.6)
            .setInteractive()
            // RI-INS-04: tocar fora encerra a edição; a nota fica no mural.
            .on('pointerup', () => !this.editor?.popup && this.fecharEditor());

        const { x, y, largura, altura } = NOTA_ABERTA;
        const papel = this.add.graphics();
        desenharPostIt(papel, x, y, largura, altura, { ...CORES_NOTA[emEdicao.cor], u: 18 });
        const escudo = this.add.zone(x, y, largura, altura).setOrigin(0).setInteractive();

        const area = document.createElement('textarea');
        area.className = 'nota-arcade';
        area.placeholder = t('mural.placeholder');
        area.maxLength = TETO_NOTA;
        area.rows = 1;
        area.value = emEdicao.texto;
        area.setAttribute('aria-label', 'Texto da nota');
        area.style.width = '325px';
        area.addEventListener('keydown', (evento) => evento.stopPropagation());
        area.addEventListener('input', () => this.aoDigitar());
        const dom = this.add.dom(640, 440, area);

        const lixeira = this.add.image(477, 611, TEX.ICON_LIXEIRA)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.confirmarExclusao());      // RI-INS-06
        this.add.zone(477, 611, 48, 48);

        const data = this.add.container(0, 0);
        const aviso = this.add.text(640, 700, '', { ...ARCADE_TEXT.aviso, color: '#ffffff' }).setOrigin(0.5);

        // Publicar é uma ação explícita: o texto só vai para o mural ao tocar em SALVAR.
        const salvar = new BotaoArcade(this, 640, 655, {
            rotulo: t('geral.salvar'), largura: 240, habilitado: temConteudo(area.value),
            aoClicar: () => this.salvarEFechar()
        });

        camada.add([fundo, papel, escudo, dom, lixeira, data, salvar, aviso]);
        this.editor = { nota: emEdicao, camada, area, dom, lixeira, data, salvar, aviso, popup: null };

        this.ajustarEditor();
        area.focus();
    }

    /** Toque em SALVAR: grava e só fecha se deu certo; em falha o aviso fica e a nota continua aberta. */
    async salvarEFechar () {
        const editor = this.editor;
        if (!editor || editor.popup || !temConteudo(editor.area.value)) return;
        editor.salvar.setHabilitado(false);
        const resultado = await this.salvar();
        if (this.editor !== editor) return;
        if (resultado?.estado === 'falhou') {
            editor.salvar.setHabilitado(true);
            return;
        }
        this.fecharEditor();
    }

    aoDigitar () {
        this.ajustarEditor();
    }

    /** Altura do texto acompanha o conteúdo, para ficar centrado na nota. */
    ajustarEditor () {
        const { area, dom, lixeira, data, nota, salvar } = this.editor;
        salvar.setHabilitado(temConteudo(area.value));
        area.style.height = 'auto';
        area.style.height = `${Math.min(area.scrollHeight, 290)}px`;
        dom.updateSize();

        lixeira.setVisible(temConteudo(area.value));               // RI-INS-05
        this.mostrarData(nota);
    }

    /** "26/10 ▪ 08:05" no rodapé da nota aberta, depois do primeiro salvamento. */
    mostrarData (nota) {
        const { data } = this.editor;
        data.removeAll(true);
        if (!nota.criadoEm) return;
        const [dia, hora] = formatarDataNota(nota.criadoEm).split(' • ');
        data.add(desenharParComPonto(this, 640, 611, dia, hora, ARCADE_TEXT.dataNota, ARCADE_HEX.TINTA));
    }

    /** Grava o texto atual; as gravações são enfileiradas para nunca criar a nota duas vezes. */
    salvar () {
        const editor = this.editor;
        if (!editor) return this.fila;
        const { nota } = editor;
        const texto = editor.area.value.trim();

        const gravacao = this.fila.then(async () => {
            const resultado = await this.gravador.salvar(nota, texto);
            if (resultado.estado === 'falhou') console.error('[Mural] falha ao salvar a nota.', resultado.classe);
            if (this.editor === editor) {
                if (resultado.estado === 'gravada') {
                    editor.aviso.setText('');
                    this.mostrarData(nota);
                } else if (resultado.estado === 'falhou') {
                    editor.aviso.setText(resultado.mensagem);
                }
            }
            return resultado;
        });
        this.fila = gravacao.then(() => undefined);
        return gravacao;
    }

    /** Fecha o editor. Nada é gravado aqui: o que não foi salvo com o botão SALVAR é descartado. */
    fecharEditor ({ redesenhar = true } = {}) {
        const editor = this.editor;
        if (!editor) return;

        editor.camada.destroy();
        this.editor = null;
        this.mural.ativa = true;
        // O toque que fechou não pode abrir a nota que está embaixo dele.
        this.mural.moveu = true;

        if (redesenhar) this.fila.then(() => this.scene.isActive() && !this.editor && this.desenharMural());
    }

    // ---------------------------------------------------------------- exclusão

    confirmarExclusao () {
        const editor = this.editor;
        if (!editor || editor.popup) return;
        editor.dom.setVisible(false);

        const popup = this.add.container(0, 0).setDepth(200);
        const fundo = this.add.rectangle(GAME.CENTER_X, GAME.CENTER_Y, GAME.LARGURA_TOTAL, GAME.HEIGHT, 0x000000, 0.45)
            .setInteractive();
        const painel = this.add.graphics();
        desenharPainel(painel, 300, 232, 680, 337);

        const fechar = () => {
            popup.destroy();
            editor.popup = null;
            editor.dom.setVisible(true);
        };

        popup.add([
            fundo, painel,
            this.add.text(640, 293, t('mural.excluir_titulo'), ARCADE_TEXT.tituloPopup).setOrigin(0.5),
            this.add.text(640, 365, t('mural.excluir_pergunta'), ARCADE_TEXT.textoPopup).setOrigin(0.5),
            this.add.text(640, 421, t('mural.excluir_aviso'), ARCADE_TEXT.textoPopup).setOrigin(0.5),
            new BotaoArcade(this, 492, 504, {
                rotulo: t('geral.cancelar'), largura: 280, aoClicar: fechar,
                cores: { fundo: ARCADE_HEX.TINTA, fio: ARCADE_HEX.TINTA, fioMeio: ARCADE_HEX.BRANCO }
            }),
            new BotaoArcade(this, 788, 504, {
                rotulo: t('mural.excluir'), largura: 280,
                cores: { fundo: ARCADE_HEX.VERMELHO, fio: ARCADE_HEX.VERMELHO, fioMeio: ARCADE_HEX.BRANCO },
                aoClicar: () => {
                    fechar();
                    this.excluir();
                }
            })
        ]);
        editor.popup = popup;
    }

    async excluir () {
        const editor = this.editor;
        const { nota } = editor;

        await this.fila;
        const resultado = await this.gravador.excluir(nota);
        if (resultado.estado === 'excluida') {
            this.fecharEditor();
        } else if (this.editor === editor) {
            console.error('[Mural] falha ao excluir a nota.', resultado.classe);
            editor.aviso.setText(resultado.mensagem);
        }
    }
}
